import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';

import { api, backingKindLabels, type BackingKind, type BankAccount, type BankStatement, type BankTransaction, type Employee } from '../api.js';
import { navigate, replaceUrl } from '../router.js';
import { pageStyles } from '../shared-styles.js';
import '../components/audit-dialog.js';
import type { AuditDialog } from '../components/audit-dialog.js';
import '../components/bank-import-dialog.js';
import { backingTag, type BankImportDialog } from '../components/bank-import-dialog.js';
import '../components/bank-transaction-dialog.js';
import type { BankTransactionDialog } from '../components/bank-transaction-dialog.js';

type View = 'transactions' | 'statements';

const kinds = Object.entries(backingKindLabels) as [BackingKind, string][];

/** Imported bank statements and their transactions, with the accountant's backing state. */
@customElement('bank-page')
export class BankPage extends LitElement {
  static styles = [
    pageStyles,
    css`
      /* Descriptions wrap inside a bounded column so the table never grows
         past the page; the column shrinks before anything overflows. */
      th.desc {
        width: 34%;
      }
      td.desc {
        min-width: 12rem;
        white-space: normal;
        overflow-wrap: anywhere;
      }
      @media (max-width: 1300px) {
        td.actions {
          white-space: normal;
        }
        td.actions wa-button {
          display: block;
        }
      }
      .totals {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem 1.5rem;
        margin-top: 0.75rem;
        color: var(--wa-color-text-quiet, #666);
        font-size: 0.9rem;
      }
      .hidden-input {
        display: none;
      }
      @media (max-width: 900px) {
        td.desc {
          max-width: none;
          min-width: 0;
        }
      }
    `,
  ];

  @state() private view: View = 'transactions';
  @state() private accountId = '';
  @state() private statementId = '';
  @state() private from = '';
  @state() private to = '';
  @state() private status: 'outstanding' | 'all' = 'outstanding';
  @state() private kind = '';
  @state() private accounts: BankAccount[] = [];
  @state() private employees: Employee[] = [];
  @state() private transactions: BankTransaction[] = [];
  @state() private statements: BankStatement[] = [];
  @state() private loading = true;
  @state() private error = '';
  private pdfTarget: BankStatement | null = null;

  connectedCallback() {
    super.connectedCallback();
    const p = new URLSearchParams(window.location.search);
    this.view = p.get('view') === 'statements' ? 'statements' : 'transactions';
    this.accountId = p.get('account') ?? '';
    this.statementId = p.get('statement') ?? '';
    this.from = p.get('from') ?? '';
    this.to = p.get('to') ?? '';
    this.status = p.get('status') === 'all' ? 'all' : 'outstanding';
    this.kind = p.get('kind') ?? '';
    void this.loadReferenceData();
    void this.load();
  }

  private async loadReferenceData() {
    try {
      [this.accounts, this.employees] = await Promise.all([api.get<BankAccount[]>('/api/bank-accounts'), api.get<Employee[]>('/api/employees')]);
    } catch (e) {
      this.error = (e as Error).message;
    }
  }

  private queryString(): string {
    const q = new URLSearchParams();
    if (this.view !== 'transactions') q.set('view', this.view);
    if (this.accountId) q.set('account', this.accountId);
    if (this.statementId) q.set('statement', this.statementId);
    if (this.from) q.set('from', this.from);
    if (this.to) q.set('to', this.to);
    if (this.view === 'transactions') {
      if (this.status !== 'outstanding') q.set('status', this.status);
      if (this.kind) q.set('kind', this.kind);
    }
    const s = q.toString();
    return s ? `?${s}` : '';
  }

  private async load() {
    this.loading = true;
    this.error = '';
    replaceUrl('/bank' + this.queryString());
    try {
      const common = new URLSearchParams();
      if (this.accountId) common.set('bank_account_id', this.accountId);
      if (this.from) common.set('from', this.from);
      if (this.to) common.set('to', this.to);
      if (this.view === 'statements') {
        this.statements = await api.get<BankStatement[]>(`/api/bank/statements?${common}`);
      } else {
        if (this.statementId) common.set('statement_id', this.statementId);
        common.set('status', this.status);
        if (this.kind) common.set('kind', this.kind);
        this.transactions = await api.get<BankTransaction[]>(`/api/bank/transactions?${common}`);
      }
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  private setView(view: View) {
    if (view === this.view) return;
    this.view = view;
    void this.load();
  }

  private set(key: 'accountId' | 'from' | 'to' | 'kind', value: string) {
    switch (key) {
      case 'accountId':
        this.accountId = value;
        this.statementId = '';
        break;
      case 'from':
        this.from = value;
        break;
      case 'to':
        this.to = value;
        break;
      case 'kind':
        this.kind = value;
        break;
    }
    void this.load();
  }

  private showStatementTransactions(s: BankStatement) {
    this.statementId = String(s.id);
    this.accountId = String(s.bank_account_id);
    this.status = 'all';
    this.view = 'transactions';
    void this.load();
  }

  private clearStatementFilter() {
    this.statementId = '';
    void this.load();
  }

  @state() private filing = false;
  @state() private notice = '';

  /** Files Treasury rows that nothing backs yet under the transfer with their payment reference. */
  private async fileTax() {
    this.filing = true;
    this.notice = '';
    try {
      const { filed } = await api.post<{ filed: number }>('/api/bank/transactions/file-tax', {});
      this.notice = filed ? `${filed} tax ${filed === 1 ? 'row' : 'rows'} filed under their transfers.` : 'Every tax row with a matching transfer is already filed.';
      if (filed) await this.load();
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.filing = false;
    }
  }

  private openImport() {
    (this.renderRoot.querySelector('bank-import-dialog') as BankImportDialog).show();
  }

  private openBacking(t: BankTransaction) {
    (this.renderRoot.querySelector('bank-transaction-dialog') as BankTransactionDialog).show(t, this.employees);
  }

  private history(entity: 'bank_statement' | 'bank_transaction', id: number, label: string) {
    void (this.renderRoot.querySelector('audit-dialog') as AuditDialog).show(entity, id, label);
  }

  private async deleteStatement(s: BankStatement) {
    const n = s.transaction_count;
    if (!confirm(`Delete statement ${s.statement_number} of ${s.account_label} (${s.statement_date}) and its ${n} ${n === 1 ? 'transaction' : 'transactions'}? Attached documents are kept.`)) return;
    try {
      await api.delete(`/api/bank/statements/${s.id}`);
      await this.load();
    } catch (e) {
      this.error = (e as Error).message;
    }
  }

  private pickPDF(s: BankStatement) {
    this.pdfTarget = s;
    (this.renderRoot.querySelector('input.hidden-input') as HTMLInputElement).click();
  }

  private async uploadPDF(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    const target = this.pdfTarget;
    input.value = '';
    if (!file || !target) return;
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      await api.upload(`/api/bank/statements/${target.id}/pdf`, fd);
      await this.load();
    } catch (err) {
      this.error = (err as Error).message;
    }
  }

  render() {
    return html`
      <div class="page-header">
        <h2>Bank</h2>
        <wa-button size="small" appearance=${this.view === 'transactions' ? 'filled' : 'outlined'} @click=${() => this.setView('transactions')}>Transactions</wa-button>
        <wa-button size="small" appearance=${this.view === 'statements' ? 'filled' : 'outlined'} @click=${() => this.setView('statements')}>Statements</wa-button>
        <span class="spacer"></span>
        <wa-button size="small" appearance="outlined" ?loading=${this.filing} @click=${this.fileTax}>File tax rows</wa-button>
        <wa-button variant="brand" @click=${this.openImport}>Import statements</wa-button>
      </div>
      ${this.notice ? html`<wa-callout variant="success">${this.notice}</wa-callout>` : nothing}
      <div class="filters">
        <wa-select label="Account" .value=${this.accountId} with-clear @change=${(e: Event) => this.set('accountId', (e.target as HTMLSelectElement).value)}>
          ${this.accounts.map((a) => html`<wa-option value=${String(a.id)}>${a.label} · ${a.currency}</wa-option>`)}
        </wa-select>
        <wa-input label="From" type="date" .value=${this.from} @change=${(e: Event) => this.set('from', (e.target as HTMLInputElement).value)}></wa-input>
        <wa-input label="To" type="date" .value=${this.to} @change=${(e: Event) => this.set('to', (e.target as HTMLInputElement).value)}></wa-input>
        ${this.view === 'transactions'
          ? html`
              <wa-select label="Show" .value=${this.status} @change=${(e: Event) => this.setStatus((e.target as HTMLSelectElement).value)}>
                <wa-option value="outstanding">Needs a document</wa-option>
                <wa-option value="all">All</wa-option>
              </wa-select>
              <wa-select label="Backing" .value=${this.kind} with-clear @change=${(e: Event) => this.set('kind', (e.target as HTMLSelectElement).value)}>
                ${kinds.map(([value, label]) => html`<wa-option value=${value}>${label}</wa-option>`)}
              </wa-select>
              ${this.statementId
                ? html`<wa-tag removable @wa-remove=${this.clearStatementFilter}>statement #${this.statementId}</wa-tag>`
                : nothing}
            `
          : nothing}
      </div>
      ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
      ${this.loading ? html`<wa-spinner></wa-spinner>` : this.view === 'statements' ? this.renderStatements() : this.renderTransactions()}
      <input class="hidden-input" type="file" accept=".pdf" @change=${this.uploadPDF} />
      <bank-import-dialog @imported=${() => void this.load()}></bank-import-dialog>
      <bank-transaction-dialog @changed=${() => void this.load()}></bank-transaction-dialog>
      <audit-dialog></audit-dialog>
    `;
  }

  private setStatus(value: string) {
    this.status = value === 'all' ? 'all' : 'outstanding';
    void this.load();
  }

  private renderTransactions() {
    const rows = this.transactions;
    if (rows.length === 0) {
      return html`<div class="empty">${this.status === 'outstanding' ? 'Nothing waits for a document.' : 'No transactions match. Import a statement to get started.'}</div>`;
    }
    const showAccount = !this.accountId;
    const debits = rows.filter((t) => t.direction === 'debit').map((t) => t.amount);
    const credits = rows.filter((t) => t.direction === 'credit').map((t) => t.amount);
    const outstanding = rows.filter((t) => t.outstanding).length;
    return html`
      <div class="table-wrap"><table class="cards">
        <thead>
          <tr>
            <th>Date</th>
            ${showAccount ? html`<th>Account</th>` : nothing}
            <th>Counterparty</th>
            <th class="desc">Description</th>
            <th class="num">Debit</th>
            <th class="num">Credit</th>
            <th class="num">Fee</th>
            <th>Backing</th>
            <th>Documents</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${rows.map(
            (t) => html`
              <tr>
                <td class="date" data-label="Date">
                  ${t.value_date}
                  <span class="sub">No. ${t.statement_number} · #${t.seq}</span>
                </td>
                ${showAccount ? html`<td data-label="Account">${t.account_label}</td>` : nothing}
                <td data-label="Counterparty">
                  ${t.counterparty_name || html`<span class="muted">—</span>`}
                  ${t.counterparty_account ? html`<span class="sub mono">${t.counterparty_account}</span>` : nothing}
                </td>
                <td class="desc" data-label="Description" title=${t.description}>
                  ${t.description}
                  ${t.reference && t.reference !== '/' ? html`<span class="sub">${t.reference}</span>` : nothing}
                </td>
                <td class=${t.direction === 'debit' && Number(t.amount) ? 'num' : 'num empty-cell'} data-label="Debit">${t.direction === 'debit' && Number(t.amount) ? `${t.amount} ${t.currency}` : ''}</td>
                <td class=${t.direction === 'credit' ? 'num' : 'num empty-cell'} data-label="Credit">${t.direction === 'credit' ? `${t.amount} ${t.currency}` : ''}</td>
                <td class=${Number(t.fee) ? 'num' : 'num empty-cell'} data-label="Fee">${Number(t.fee) ? t.fee : ''}</td>
                <td data-label="Backing">
                  ${backingTag(t.backing_kind, t.employee_name, t.outstanding, t.tax_of)}
                  ${t.tax_count ? html`<span class="sub">covers ${t.tax_count === 1 ? 'a tax row' : `${t.tax_count} tax rows`}</span>` : nothing}
                  ${t.backing_note ? html`<span class="sub">${t.backing_note}</span>` : nothing}
                </td>
                <td class=${t.documents.length ? '' : 'empty-cell'} data-label="Documents">
                  ${t.documents.map((d) => html`<div><a href=${`/api/documents/${d.id}/download`} target="_blank" rel="noopener">${d.title}</a></div>`)}
                </td>
                <td class="actions">
                  <wa-button size="small" appearance="plain" @click=${() => this.openBacking(t)}>Backing</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.history('bank_transaction', t.id, `${t.value_date} ${t.amount} ${t.currency}`)}>History</wa-button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table></div>
      <div class="totals">
        <span>${rows.length} rows</span>
        <span>debits ${sum(debits)}</span>
        <span>credits ${sum(credits)}</span>
        <span>fees ${sum(rows.map((t) => t.fee))}</span>
        <span class=${outstanding ? 'warn' : ''}>${outstanding} outstanding</span>
      </div>
    `;
  }

  private renderStatements() {
    const rows = this.statements;
    if (rows.length === 0) return html`<div class="empty">No statements imported yet. Use "Import statements" with the bank's CSV export and its PDF.</div>`;
    return html`
      <div class="table-wrap"><table class="cards">
        <thead>
          <tr>
            <th>Date</th>
            <th>Account</th>
            <th>No.</th>
            <th class="num">Opening</th>
            <th class="num">Debits</th>
            <th class="num">Credits</th>
            <th class="num">Fees</th>
            <th class="num">Closing</th>
            <th class="num">Rows</th>
            <th>PDF</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${rows.map(
            (s) => html`
              <tr>
                <td class="date" data-label="Date">
                  ${s.statement_date}
                  <span class="sub">imported ${s.imported_at.slice(0, 10)} by ${s.imported_by}</span>
                </td>
                <td data-label="Account">${s.account_label} <span class="muted">${s.currency}</span></td>
                <td data-label="No.">
                  ${s.statement_number}
                  ${s.warnings.length ? html`<wa-tag size="small" variant="warning" title=${s.warnings.map((w) => w.message).join('\n')}>${s.warnings.length} ${s.warnings.length === 1 ? 'warning' : 'warnings'}</wa-tag>` : nothing}
                </td>
                <td class="num" data-label="Opening">${s.opening_balance}</td>
                <td class="num" data-label="Debits">${s.total_debits}</td>
                <td class="num" data-label="Credits">${s.total_credits}</td>
                <td class="num" data-label="Fees">${s.total_fees}</td>
                <td class="num" data-label="Closing">${s.closing_balance}</td>
                <td class="num" data-label="Rows">
                  ${s.transaction_count}
                  ${s.outstanding_count ? html`<span class="sub warn">${s.outstanding_count} outstanding</span>` : nothing}
                </td>
                <td class="file" data-label="PDF">
                  ${s.document_id
                    ? html`<a href=${`/api/documents/${s.document_id}/download`} target="_blank" rel="noopener">${s.document_filename}</a>`
                    : html`<wa-button size="small" appearance="plain" @click=${() => this.pickPDF(s)}>Attach PDF</wa-button>`}
                </td>
                <td class="actions">
                  <wa-button size="small" appearance="plain" @click=${() => this.showStatementTransactions(s)}>Transactions</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => navigate(`/documents?bank_account_id=${s.bank_account_id}`)}>Documents</wa-button>
                  <wa-button size="small" appearance="plain" @click=${() => this.history('bank_statement', s.id, `Statement ${s.statement_number}`)}>History</wa-button>
                  <wa-button size="small" appearance="plain" variant="danger" @click=${() => this.deleteStatement(s)}>Delete</wa-button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table></div>
    `;
  }
}

function sum(values: string[]): string {
  const cents = values.reduce((acc, v) => acc + Math.round(Number(v) * 100), 0);
  return (cents / 100).toFixed(2);
}

declare global {
  interface HTMLElementTagNameMap {
    'bank-page': BankPage;
  }
}
